// core/router.js
// Hash-based SPA router for skill navigation

class Router {
    constructor() {
        this.routes = new Map();        // route pattern -> handler
        this.currentRoute = null;
        this.currentParams = {};
        this.beforeHooks = [];
        this.afterHooks = [];
        this.initialized = false;
    }

    /**
     * Register a route pattern with a handler
     * @param {string} pattern - Route pattern like '#/:skill' or '#/:skill/:tab'
     * @param {Function} handler - Async function called with params object
     */
    register(pattern, handler) {
        this.routes.set(pattern, handler);
    }

    /**
     * Parse hash into route match with params
     * @param {string} hash - URL hash like '#/mining/smelting'
     * @returns {Object|null} - { pattern, handler, params } or null if no match
     */
    parse(hash) {
        // Normalize hash
        if (!hash || hash === '#' || hash === '#/') {
            hash = '#/';
        }

        // Try each registered route pattern
        for (const [pattern, handler] of this.routes) {
            const match = this.matchPattern(pattern, hash);
            if (match !== null) {
                return { pattern, handler, params: match };
            }
        }
        return null;
    }

    /**
     * Match a pattern against a hash, extracting params
     * @param {string} pattern - Route pattern with :param placeholders
     * @param {string} hash - Actual URL hash
     * @returns {Object|null} - Params object or null if no match
     */
    matchPattern(pattern, hash) {
        // Normalize both to remove #/ prefix before splitting
        const normalizedPattern = pattern.replace(/^#\/?/, '');
        const normalizedHash = hash.replace(/^#\/?/, '');

        // Split into segments
        const patternParts = normalizedPattern.split('/').filter(Boolean);
        const hashParts = normalizedHash.split('/').filter(Boolean);

        // Special case: root route (both empty after normalization)
        if (pattern === '#/' && hashParts.length === 0) {
            return {};
        }

        // Must have same number of segments
        if (patternParts.length !== hashParts.length) {
            return null;
        }

        const params = {};

        for (let i = 0; i < patternParts.length; i++) {
            const patternPart = patternParts[i];
            const hashPart = hashParts[i];

            if (patternPart.startsWith(':')) {
                // This is a parameter - capture it
                const paramName = patternPart.slice(1);
                params[paramName] = decodeURIComponent(hashPart);
            } else if (patternPart !== hashPart) {
                // Literal segment doesn't match
                return null;
            }
        }

        return params;
    }

    /**
     * Navigate to a route
     * @param {string} hash - Target hash like '#/mining'
     * @param {boolean} replace - Replace current history entry instead of pushing
     */
    navigate(hash, replace = false) {
        // Ensure hash starts with #
        if (!hash.startsWith('#')) {
            hash = '#' + hash;
        }

        if (replace) {
            history.replaceState(null, '', hash);
        } else {
            history.pushState(null, '', hash);
        }

        this.handleRoute();
    }

    /**
     * Handle the current route
     */
    async handleRoute() {
        const hash = window.location.hash || '#/';
        const result = this.parse(hash);

        // Store previous route for hooks
        const previousRoute = this.currentRoute;
        const previousParams = this.currentParams;

        // Run before hooks - any can cancel navigation by returning false
        for (const hook of this.beforeHooks) {
            try {
                const shouldContinue = await hook(result, { route: previousRoute, params: previousParams });
                if (shouldContinue === false) {
                    return;
                }
            } catch (error) {
                console.error('[Router] Before hook error:', error);
            }
        }

        if (result) {
            this.currentRoute = result.pattern;
            this.currentParams = result.params;

            try {
                await result.handler(result.params);
            } catch (error) {
                console.error('[Router] Handler error:', error);
            }
        } else {
            // No matching route - navigate to default
            console.warn('[Router] No matching route for:', hash);
            this.navigate('#/', true);
            return;
        }

        // Run after hooks
        for (const hook of this.afterHooks) {
            try {
                await hook(result, { route: previousRoute, params: previousParams });
            } catch (error) {
                console.error('[Router] After hook error:', error);
            }
        }
    }

    /**
     * Initialize the router - call once on app start
     */
    init() {
        if (this.initialized) {
            console.warn('[Router] Already initialized');
            return;
        }

        // Listen for hash changes
        window.addEventListener('hashchange', () => this.handleRoute());

        // Listen for popstate (back/forward buttons)
        window.addEventListener('popstate', () => this.handleRoute());

        this.initialized = true;

        // Handle initial route
        if (!window.location.hash) {
            // No hash - get last viewed skill from localStorage or default to mining
            const lastSkill = localStorage.getItem('idleRealms_lastSkill') || 'mining';
            this.navigate(`#/${lastSkill}`, true);
        } else {
            this.handleRoute();
        }
    }

    /**
     * Add a before-navigation hook
     * @param {Function} hook - Async function(nextRoute, previousRoute) -> boolean
     */
    beforeEach(hook) {
        this.beforeHooks.push(hook);
    }

    /**
     * Add an after-navigation hook
     * @param {Function} hook - Async function(currentRoute, previousRoute)
     */
    afterEach(hook) {
        this.afterHooks.push(hook);
    }

    /**
     * Get current route info
     * @returns {Object} - { route, params }
     */
    getCurrentRoute() {
        return {
            route: this.currentRoute,
            params: { ...this.currentParams }
        };
    }

    /**
     * Check if a route pattern is registered
     * @param {string} pattern - Route pattern
     * @returns {boolean}
     */
    hasRoute(pattern) {
        return this.routes.has(pattern);
    }
}

// Export singleton instance
export const router = new Router();
export default router;
